import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  AdminAreaList,
  AdminAuditLogList,
  AdminSegmentList,
  AdminUserCreateResponse,
  AdminUserDetail,
  AdminUserList,
} from "@/api/types";

// ── Usuarios ──────────────────────────────────────────────────────────────────

export function useAdminUsers(filters: { q?: string; is_active?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.is_active !== undefined) params.set("is_active", String(filters.is_active));
  const qs = params.toString();
  return useQuery({
    queryKey: ["admin", "users", filters],
    queryFn: ({ signal }) =>
      api<AdminUserList>(`/api/v1/admin/users${qs ? `?${qs}` : ""}`, { signal }),
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: ({ signal }) => api<AdminUserDetail>(`/api/v1/admin/users/${userId}`, { signal }),
    enabled: !!userId,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; role?: string }) =>
      api<AdminUserCreateResponse>("/api/v1/admin/users", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useUpdateUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username?: string; is_active?: boolean; telegram_chat_id?: string | null }) =>
      api<AdminUserDetail>(`/api/v1/admin/users/${userId}`, { method: "PUT", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useDeleteUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>(`/api/v1/admin/users/${userId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useAssignSegments(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (segment_ids: string[]) =>
      api<AdminUserDetail>(`/api/v1/admin/users/${userId}/segments`, {
        method: "PUT",
        body: { segment_ids },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", userId] }),
  });
}

export function useAssignAreaRoles(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Array<{ area_id: string; role: string }>) =>
      api<AdminUserDetail>(`/api/v1/admin/users/${userId}/area-roles`, {
        method: "PUT",
        body: { items },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", userId] }),
  });
}

export function useAssignGlobalRoles(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roles: string[]) =>
      api<AdminUserDetail>(`/api/v1/admin/users/${userId}/global-roles`, {
        method: "PUT",
        body: { roles },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", userId] }),
  });
}

export function usePasswordReset(userId: string) {
  return useMutation({
    mutationFn: () =>
      api<{ temp_password: string; must_change_password: boolean }>(
        `/api/v1/admin/users/${userId}/password-reset`,
        { method: "POST" },
      ),
  });
}

export function useLockUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<AdminUserDetail>(`/api/v1/admin/users/${userId}/lock`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useUnlockUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<AdminUserDetail>(`/api/v1/admin/users/${userId}/unlock`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useDisable2FA(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<AdminUserDetail>(`/api/v1/admin/users/${userId}/2fa/disable`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users", userId] }),
  });
}

export function useReset2FA(userId: string) {
  return useMutation({
    mutationFn: () =>
      api<{ qr_data_uri: string; totp_secret: string }>(
        `/api/v1/admin/users/${userId}/2fa/reset`,
        { method: "POST" },
      ),
  });
}

export function useUserAuditLog(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId, "audit"],
    queryFn: ({ signal }) =>
      api<AdminAuditLogList>(`/api/v1/admin/users/${userId}/audit?limit=50`, { signal }),
    enabled: !!userId,
  });
}

// ── Segmentos ─────────────────────────────────────────────────────────────────

export function useAdminSegments(params: { q?: string; active_only?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.active_only) qs.set("active_only", "true");
  return useQuery({
    queryKey: ["admin", "segments", params],
    queryFn: ({ signal }) =>
      api<AdminSegmentList>(`/api/v1/admin/segments${qs.toString() ? `?${qs}` : ""}`, { signal }),
  });
}

export function useCreateSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; name: string; description?: string }) =>
      api("/api/v1/admin/segments", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "segments"] }),
  });
}

export function useUpdateSegment(segmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; description?: string; is_active?: boolean }) =>
      api(`/api/v1/admin/segments/${segmentId}`, { method: "PUT", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "segments"] }),
  });
}

export function useSegmentAreas(segmentId: string | null) {
  return useQuery({
    queryKey: ["admin", "segments", segmentId, "areas"],
    queryFn: ({ signal }) =>
      api<AdminAreaList>(`/api/v1/admin/segments/${segmentId}/areas`, { signal }),
    enabled: !!segmentId,
  });
}

export function useAdminAreas() {
  return useQuery({
    queryKey: ["admin", "areas"],
    queryFn: ({ signal }) => api<AdminAreaList>("/api/v1/admin/areas", { signal }),
  });
}

// ── Audit log ─────────────────────────────────────────────────────────────────

export function useAdminAuditLog(filters: {
  action?: string;
  target_type?: string;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (filters.action) qs.set("action", filters.action);
  if (filters.target_type) qs.set("target_type", filters.target_type);
  qs.set("limit", String(filters.limit ?? 50));
  return useQuery({
    queryKey: ["admin", "audit-log", filters],
    queryFn: ({ signal }) =>
      api<AdminAuditLogList>(`/api/v1/admin/audit-log?${qs}`, { signal }),
  });
}
